import { useEffect, useState } from "react";
import { MagnifyingGlass, X, CaretDown, CaretUp, Paperclip, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { AttachmentThumb } from "@/components/TaskLogPanel";

/**
 * Global sökning över alla momentloggar i organisationen.
 * Läsläge — inga add/edit-knappar. Används på både Tasks-sidan (admin) och
 * Dashboard (alla, inkl. anställda).
 *
 * Props:
 *   testIdPrefix — prefix för alla data-testid (default "global-tasklog")
 *   placeholder — override för placeholder-texten
 *   compact — trimmar överflödig marginal när komponenten sitter inbäddad
 */
export default function GlobalTaskLogSearch({
  testIdPrefix = "global-tasklog",
  placeholder = "Sök i alla momentloggar — t.ex. 'ekträ', 'spricka', 'hammare', 'gulmålad hall'…",
  compact = false,
}) {
  const [searchText, setSearchText] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const q = searchText.trim();
    if (q === activeSearch) return;
    const h = setTimeout(async () => {
      setActiveSearch(q);
      if (!q) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const r = await api.get("/task-logs/search", { params: { q, limit: 50 } });
        setResults(r.data?.entries || []);
      } catch {
        toast.error("Kunde inte söka i momentloggen");
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(h);
  }, [searchText, activeSearch]);

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("sv-SE", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className="relative">
        <MagnifyingGlass
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
        />
        <input
          data-testid={`${testIdPrefix}-search`}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={placeholder}
          className="tk-input !pl-10 !py-3"
        />
        {searchText && (
          <button
            data-testid={`${testIdPrefix}-search-clear`}
            onClick={() => {
              setSearchText("");
              setActiveSearch("");
              setResults([]);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary p-1"
            aria-label="Rensa sökning"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {activeSearch && (
        <div className="space-y-3" data-testid={`${testIdPrefix}-search-results`}>
          <div className="text-xs text-text-muted">
            {searching
              ? `Söker efter "${activeSearch}"…`
              : `${results.length} träff${results.length === 1 ? "" : "ar"} på "${activeSearch}"`}
          </div>

          {!searching && results.length === 0 && (
            <EmptyState
              testId={`${testIdPrefix}-search-empty`}
              icon={MagnifyingGlass}
              title="Inga träffar"
              description="Prova ett annat ord — sökningen matchar text, författare, filnamn och osynliga AI-taggar på uppladdade bilder."
            />
          )}

          {results.map((e) => {
            const isOpen = expanded === e.entry_id;
            return (
              <div
                key={e.entry_id}
                data-testid={`${testIdPrefix}-result-${e.entry_id}`}
                className="tk-card overflow-hidden"
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : e.entry_id)}
                  className="w-full text-left p-4 flex items-start gap-3 hover:bg-surface-hover transition"
                >
                  <div
                    className="w-1.5 rounded-full flex-shrink-0 self-stretch min-h-[48px]"
                    style={{ background: e.task_color || "#5C4033" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
                        style={{ background: e.task_color || "#5C4033" }}
                      >
                        {e.task_name}
                      </span>
                      {e.object_name ? (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-surface-hover border border-app inline-flex items-center gap-1">
                          <Buildings size={11} /> {e.object_name}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-surface-hover border border-dashed border-app text-text-muted italic">
                          okänt objekt
                        </span>
                      )}
                      <span className="text-xs text-text-muted">
                        {e.author_name} · {formatDate(e.created_at)}
                      </span>
                      {(e.attachments || []).length > 0 && (
                        <span className="text-xs text-text-muted inline-flex items-center gap-1">
                          <Paperclip size={12} /> {e.attachments.length}
                        </span>
                      )}
                    </div>
                    {!isOpen && e.text && (
                      <p className="text-sm mt-1.5 line-clamp-2 whitespace-pre-wrap">{e.text}</p>
                    )}
                    {!isOpen && !e.text && (e.attachments || []).length > 0 && (
                      <p className="text-xs text-text-muted italic mt-1.5">
                        Endast bilaga — klicka för att visa
                      </p>
                    )}
                  </div>
                  <span className="text-text-muted self-center pl-1">
                    {isOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-app bg-surface-alt/40">
                    {e.text && (
                      <p className="text-sm whitespace-pre-wrap mt-3">{e.text}</p>
                    )}
                    {(e.attachments || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {e.attachments.map((att) => (
                          <AttachmentThumb
                            key={att.file_id}
                            task_id={e.task_id}
                            entry_id={e.entry_id}
                            attachment={att}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
